import { Box, Breadcrumbs, Button, Grid, Link, Typography } from '@mui/material'
import { LargeCard } from 'Components/Cards/Large'
import { ChildDuplicateDisplay } from 'Components/ChildDetails/ChildDuplicateDisplay'
import ButtonGroup, { type buttonOption } from 'Components/Button/ButtonGroup'
import { TextAreaWithLabel } from 'Components/TextArea'
import { useState } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useNavigate, generatePath } from 'react-router'
import { useAppSelector } from 'hooks/reduxHooks'
import { type ChildDuplicate, clear, dupeSelector, type duplicateResolution, originalSelector, duplicateStateSelector, useApproveDuplicateMutation, useDeclineDuplicateMutation, useCreateDuplicateMutation, duplicateSource } from 'slices/duplicateChildSlice'
import { toast } from 'react-toastify'
import { QueryStatus } from '@reduxjs/toolkit/query'
import { useDispatch } from 'react-redux'
import { usePermissions } from 'hooks/use-permissions'
import { CARENETWORK_MANAGEMENT_DETAILS } from 'Pages/Routes'
import { DiscardDuplicate } from 'Components/ChildDetails/DiscardDuplicate'
import { sendPost } from 'hooks/use-fetch'

export interface DenialAccessPermissions {
  grantAccess: boolean
  portalView: boolean
  portalModify: boolean
}

export function DuplicateResolution (): JSX.Element {
  const dupeRecord = useAppSelector(dupeSelector)
  const dupeState = useAppSelector(duplicateStateSelector)
  const existingRecord = useAppSelector(originalSelector)
  const [explanation, setExplanation] = useState('')
  const [isDiscarding, setIsDiscarding] = useState(false)
  const isNew = (dupeRecord?.id ?? 0) === 0
  const isEdit = (dupeRecord?.childId ?? 0) !== 0
  const dispatch = useDispatch()
  const [createDupe, request1] = useCreateDuplicateMutation()
  const [approveDupe, request3] = useApproveDuplicateMutation()
  const [declineDupe, request4] = useDeclineDuplicateMutation()
  const isDone = request1.status === QueryStatus.pending || request1.status === QueryStatus.fulfilled ||
    request3.status === QueryStatus.pending || request3.status === QueryStatus.fulfilled ||
    request4.status === QueryStatus.pending || request4.status === QueryStatus.fulfilled
  const [resolutionChoice, setResolutionChoice] = useState<number>(isNew ? 1 : 0)
  const hasPermission = usePermissions().ISDs_DuplicateHandling

  const nav = useNavigate()
  const handleClick = (val: number): void => {
    if ((val === 0 && !isNew) || val === 1 || val === 2) {
      setResolutionChoice(val)
    }
  }
  const goBack = (): void => {
    nav(-1)
  }

  const validate = async (): Promise<void> => {
    const errors = []
    if (resolutionChoice !== 2) {
      if (explanation == null || explanation === '') {
        errors.push('Explanation of duplicate creation is required')
      }
      if (dupeRecord == null) {
        errors.push('Duplicate information was not loaded correctly. Please go back and save again.')
      }
    }
    if (errors.length > 0) {
      errors.map(e => toast.error(e))
    } else {
      if (dupeRecord != null && !isDone) {
        if (resolutionChoice === 0) {
          setIsDiscarding(true)
        } else {
          await handleSubmit()
        }
      }
    }
  }

  const handleSubmit = async (): Promise<void> => {
    const dupeWithMatchers: ChildDuplicate = { ...dupeRecord as ChildDuplicate, matchedValues: existingRecord[0]?.matchedValues ?? [] }
    const resolution: duplicateResolution = {
      reasoning: explanation,
      duplicateId: dupeWithMatchers?.id ?? null,
      duplicate: dupeWithMatchers,
      caregiverID: 0,
      matchedChildID: existingRecord[0]?.childId ?? 0,
      matchedChildNickName: dupeRecord?.nickName ?? null,
      source: dupeRecord?.source ?? duplicateSource.ChildEntry,
      childID: dupeRecord?.childId ?? null,
      providerID: null

    }
    let success: boolean = false
    let errors: string = ''
    let successText = 'Duplicate record created for review.'
    let newChildId: number | null = null
    if (resolutionChoice === 0) {
      if (isNew) {
        success = true
        errors = ''
        successText = 'Record discarded.'
      } else {
        const { data, error } = await declineDupe({ resolution, dupeState })
        success = data as boolean
        errors = error?.toString() ?? ''
        newChildId = resolution.matchedChildID
        successText = 'Duplicate record declined.'
      }
    } else if (resolutionChoice === 1) {
      let dupeId = 0
      if (isNew) {
        const { data, error } = await createDupe(resolution)
        dupeId = data ?? 0
        success = (data ?? 0) !== 0
        errors = error?.toString() ?? ''
        resolution.reasoning = 'Auto approved by entering user'
      } else {
        success = true
        errors = ''
        dupeId = resolution.duplicateId ?? 0
      }
      if (success) {
        resolution.duplicateId = dupeId ?? 0
        const { data: data2, error: error2 } = await approveDupe(resolution)
        success = (data2 ?? 0) !== 0
        newChildId = data2 ?? null
        errors = error2?.toString() ?? ''
        successText = 'Child record created.'
      }
    } else if (resolutionChoice === 2) {
      await sendPost('/CareNetworkEdit/AddSibling', { childID: dupeRecord?.siblingId, siblingId: resolution.matchedChildID })
      newChildId = Number(dupeRecord?.siblingId ?? '0')
      success = true
      errors = ''
      successText = 'Existing child was used.'
    }

    if (success && newChildId != null) {
      toast.success(successText)
      dispatch(clear())
      const editChild = generatePath(CARENETWORK_MANAGEMENT_DETAILS, { id: newChildId.toString() ?? '' })
      nav(editChild)
    } else {
      toast.error('Could not save duplicate record.' + errors)
    }
  }

  const buttonOpts: buttonOption[] = [{ id: 0, name: `Discard ${(isEdit ? 'Edit' : 'New Record')}`, disabled: isNew }, { id: 1, name: `${(isEdit ? 'Apply Edit' : 'Create New Record')}` }]
  if (dupeRecord?.siblingId != null) {
    buttonOpts.push({ id: 2, name: 'Use Existing Record' })
  }

  const denied = !hasPermission
  if (denied) {
    return <Box>You do not have permission to resolve duplicate requests</Box>
  }

  return <Box width={'100%'} >

    <Typography fontWeight={'bold'} fontSize={'32px'}>Resolve Duplicate</Typography>
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
      <ArrowBackIcon onClick={goBack} sx={{ alignSelf: 'center', cursor: 'pointer', height: '16px', width: '16px' }} />
      <Breadcrumbs >
        <Link onClick={goBack} sx={{ fontSize: '14px', textTransform: 'none', color: 'inherit', textDecorationLine: 'none', cursor: 'pointer' }}>Child Details</Link>
      </Breadcrumbs>
    </Box>

    {isDiscarding
      ? <DiscardDuplicate
          dupeRecord={dupeRecord}
          existingRecord={existingRecord[0] ?? null}
          accessPermissions={{
            grantAccess: dupeState?.grantAccess ?? false,
            portalView: dupeState?.portalView ?? false,
            portalModify: dupeState?.portalModify ?? false
          }}
          onSubmit={handleSubmit}
          goBack={() => { setIsDiscarding(false) }}
      />
      : <LargeCard customContentWidth fullWidth
          contentSx={{ p: '20px' }}
          sx={{ width: 'fit-content', borderRadius: '15px' }}
          content={
            <Grid container spacing={1} gap={1} sx={{ display: 'flex', flexDirection: 'column', m: '0px', p: '0px', width: 'auto' }}>
              <Grid>
                <Typography fontWeight={'bold'} fontSize={'18px'}>This child may be a duplicate, how would you like to proceed?</Typography>
              </Grid>
              {dupeState?.resolved !== true && (
                <Grid>
                  <ButtonGroup value={resolutionChoice} options={buttonOpts} setValue={handleClick} />
                </Grid>
              )}
              <Grid>
                <ChildDuplicateDisplay />
              </Grid>
              {resolutionChoice !== 2 && <Grid>
                <TextAreaWithLabel
                  disabled={dupeState?.resolved}
                  label={'Please provide an explanation'}
                  name={'explanation'}
                  onChange={(e: any): void => { setExplanation(e.target.value) }}
                  value={dupeState?.resolved === true ? dupeState?.reasoning : explanation}
                  dataTestId='explanation-input'
                />
              </Grid>
              }
              <Grid sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <Button sx={{ textTransform: 'none', px: '35px' }} data-testid='ReturnButton' variant='outlined' onClick={goBack}>Go Back</Button>
                {dupeState?.resolved !== true && (
                  <Button data-testid='resolveAction' sx={{ textTransform: 'none', px: '35px' }} variant='contained' disabled={isDone} onClick={validate}>Continue</Button>
                )}
              </Grid>
            </Grid>}
      />
    }
  </Box>
}
